// Strategies over limit: driving-measure utilisation above its max, or a VaR threshold breached. Mirrors the
// RC-based check in StrategyBubble (utilisation = exposure / allocated risk capital).
export const isOverLimit = (strategy, drivingMeasure = 'GMV') => {
  const measure = String(drivingMeasure).toLowerCase();
  if (!['gmv', 'lmv', 'smv', 'nmv'].includes(measure)) return false;
  const exposure = strategy?.[measure];
  const riskCapital = strategy?.risk_capital;
  if (!riskCapital || exposure == null) return false;
  const utilization = ((measure === 'nmv' ? Math.abs(exposure) : exposure) / riskCapital) * 100;
  return utilization > (strategy[`max_${measure}_pct`] || 100);
};

// VaR and its threshold are both negative; the % of limit used is var / threshold (e.g. a VaR of
// -4M against a -5M threshold is 80%). Null when either is missing or the threshold is not a loss.
export const varLimitUsedPct = (varValue, threshold) => {
  if (varValue == null || threshold == null || !Number.isFinite(varValue) || !Number.isFinite(threshold) || threshold >= 0) return null;
  return (varValue / threshold) * 100;
};

// Same bands as the exposure limits (getUtilizationStatus): over 100% breached, 95-100% near limit (warning).
export const varLimitStatus = (pct) => {
  if (pct == null) return 'no-limit';
  if (pct > 100) return 'critical';
  if (pct > 95) return 'warning';
  return 'good';
};

export const isVarBreached = (strategy) =>
  varLimitStatus(varLimitUsedPct(strategy?.var95, strategy?.var95Threshold)) === 'critical' ||
  varLimitStatus(varLimitUsedPct(strategy?.var99, strategy?.var99Threshold)) === 'critical';

export const countOverLimit = (strategies = [], thresholds = []) => {
  const byType = Object.fromEntries(thresholds.map((t) => [t.strategy_type, t]));
  return strategies.filter(
    (s) => isOverLimit(s, byType[s.strategy_type_name]?.driving_measure || 'GMV') || isVarBreached(s),
  ).length;
};

// ---- Attention list: what is over or close to its limit, worst first ----------------------------------------

const DRIVING_MEASURES = ['gmv', 'lmv', 'smv', 'nmv'];

// Driving-measure utilisation (% of risk capital) against its max; usedPct is utilisation / max,
// e.g. 623% against a 500% max is 125% of the limit.
export const exposureLimitUsed = (strategy, drivingMeasure) => {
  const measure = String(drivingMeasure).toLowerCase();
  if (!DRIVING_MEASURES.includes(measure)) return null;
  const exposure = strategy?.[measure];
  const riskCapital = strategy?.risk_capital;
  if (!riskCapital || exposure == null) return null;
  const utilization = ((measure === 'nmv' ? Math.abs(exposure) : exposure) / riskCapital) * 100;
  const max = strategy[`max_${measure}_pct`] || 100;
  return { measure: measure.toUpperCase(), utilization, max, usedPct: (utilization / max) * 100 };
};

const severityOf = (usedPct) => (usedPct > 100 ? 'critical' : usedPct > 95 ? 'warning' : null);

/**
 * Items needing attention across strategies: exposure above its max (critical) or above 95% of it (warning),
 * and VaR 95% / 99% above 100% / 95% of its threshold. Critical first, then by how far past the limit.
 * @returns {Array<{strategy_type_name: string, severity: 'critical'|'warning', title: string, detail: string, usedPct: number}>}
 */
export const getAttentionItems = (strategies = [], thresholds = []) => {
  const byType = Object.fromEntries(thresholds.map((t) => [t.strategy_type, t]));
  const items = [];
  for (const s of strategies) {
    const name = s.strategy_type_name;
    const exposure = exposureLimitUsed(s, byType[name]?.driving_measure || 'GMV');
    const exposureSeverity = exposure && severityOf(exposure.usedPct);
    if (exposureSeverity) {
      items.push({
        strategy_type_name: name,
        severity: exposureSeverity,
        title: `${exposure.measure} ${exposure.utilization.toFixed(0)}% of RC`,
        detail: `limit ${exposure.max.toFixed(0)}%`,
        usedPct: exposure.usedPct,
      });
    }
    for (const [label, value, threshold] of [
      ['VaR 95%', s.var95, s.var95Threshold],
      ['VaR 99%', s.var99, s.var99Threshold],
    ]) {
      const usedPct = varLimitUsedPct(value, threshold);
      const severity = usedPct != null && severityOf(usedPct);
      if (severity) {
        items.push({ strategy_type_name: name, severity, title: label, detail: `${usedPct.toFixed(0)}% of limit`, usedPct });
      }
    }
  }
  return items.sort(
    (a, b) => (a.severity === b.severity ? 0 : a.severity === 'critical' ? -1 : 1) || b.usedPct - a.usedPct,
  );
};

// Worst-first score for ordering strategy cards: higher means more severe, 0 means nothing to flag.
export const strategySeverityScore = (items, strategyTypeName) => {
  const own = items.filter((i) => i.strategy_type_name === strategyTypeName);
  if (!own.length) return 0;
  return Math.max(...own.map((i) => (i.severity === 'critical' ? 1000 : 500) + i.usedPct));
};

export const strategyCardId = (strategyTypeName) =>
  `risk-card-${String(strategyTypeName).toLowerCase().replace(/[^a-z0-9]+/g, '-')}`;
